import React, { useState } from 'react';
import { z } from 'zod';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField, SelectField } from 'components/form';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';

import { useExternalDashboards } from '../../../api';
import { SELECT_OPTION_DASHBOARD_ICON, SELECT_OPTION_INTEGRATION_TYPE } from '../../../constants';

const dashboardInfoFormValuesSchema = z.object({
  type: z.string(),
  icon: z.string(),
  description: z.string(),
  name: z.string().min(3).max(100),
  externalId: z.string().min(1).max(100),
});

type DashboardInfoFormValues = z.infer<typeof dashboardInfoFormValuesSchema>;

export const ExternalDashboardDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.ExternalDashboardDialog);

  const [loading, setIsLoading] = useState(!!data?.id);

  const { handleCreate, handleFetchDashboard, handleUpdate } = useExternalDashboards();

  const handleGetFormInitialValues = async (): Promise<DashboardInfoFormValues> => {
    if (data?.id) {
      return handleFetchDashboard({ id: data.id })
        .then(dashboard => {
          if (!dashboard) return { name: '', icon: '', type: 'TREVOR', externalId: '', description: '' };

          return {
            name: dashboard.name,
            type: dashboard.type,
            icon: dashboard.icon ?? '',
            externalId: dashboard.externalId,
            description: dashboard.description,
          };
        })
        .finally(() => setIsLoading(false));
    }

    return { name: '', icon: '', type: 'TREVOR', externalId: '', description: '' };
  };

  const { control, handleSubmit } = useForm<DashboardInfoFormValues>({
    mode: 'all',
    defaultValues: handleGetFormInitialValues,
    resolver: zodResolver(dashboardInfoFormValuesSchema),
  });

  const handleSave = async (formData: DashboardInfoFormValues) => {
    handleClose();

    if (data?.id) {
      await handleUpdate({ ...formData, id: data.id });
    } else {
      await handleCreate(formData);
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth onClose={handleClose}>
      <DialogHeader handleClose={handleClose} label="Add External Dashboard" />

      <DialogContent>
        <RenderWithFallback
          isLoading={loading}
          fallBack={
            <Stack height="10rem" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }
        >
          <Stack gap="1rem" paddingTop="0.5rem">
            <InputField
              label="Name"
              control={control}
              name="name"
              inputProps={{ variant: 'outlined', required: true }}
            />

            <InputField label="Description" control={control} name="description" inputProps={{ variant: 'outlined' }} />

            <InputField
              name="externalId"
              control={control}
              label="ExternalId"
              inputProps={{ variant: 'outlined', required: true }}
            />

            <SelectField
              required
              name="icon"
              label="Icon"
              control={control}
              variant="outlined"
              options={SELECT_OPTION_DASHBOARD_ICON}
            />

            <SelectField
              required
              name="type"
              label="Type"
              control={control}
              variant="outlined"
              disabled={!!data?.id}
              options={SELECT_OPTION_INTEGRATION_TYPE}
            />
          </Stack>
        </RenderWithFallback>
      </DialogContent>

      <DialogActions>
        <Button size="small" color="primary" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>

        <Button size="small" color="primary" variant="contained" onClick={handleSubmit(handleSave)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
